/**
 * Minimal ABI for BondingCurveMarket.sol — only the entries the trading panel needs.
 *
 * Source: contracts-fair-launch/contracts/BondingCurveMarket.sol
 *
 * Each fair-launch market has its own BondingCurveMarket contract address
 * (stored in the DB as `marketAddress`). The factory address and market address
 * are different contracts — never confuse them.
 */
export const bondingCurveMarketAbi = [
  // ── Oracle attestation (view) ─────────────────────────────────────────────────
  {
    name: "oracleRoundId",
    type: "function",
    stateMutability: "view",
    inputs: [],
    outputs: [{ name: "", type: "uint80" }],
  },

  // ── Quote (view) ─────────────────────────────────────────────────────────────
  {
    name: "quoteBuy",
    type: "function",
    stateMutability: "view",
    inputs: [{ name: "ethIn", type: "uint256" }],
    outputs: [
      { name: "tokensOut", type: "uint256" },
      { name: "fee",       type: "uint256" },
    ],
  },
  {
    name: "quoteSell",
    type: "function",
    stateMutability: "view",
    inputs: [{ name: "tokensIn", type: "uint256" }],
    outputs: [
      { name: "netEth", type: "uint256" },
      { name: "fee",    type: "uint256" },
    ],
  },

  // ── Trade (write) ─────────────────────────────────────────────────────────────
  {
    name: "buy",
    type: "function",
    stateMutability: "payable",
    inputs: [
      { name: "minTokensOut", type: "uint256" },
      { name: "deadline",     type: "uint256" },
    ],
    outputs: [],
  },
  {
    name: "sell",
    type: "function",
    stateMutability: "nonpayable",
    inputs: [
      { name: "tokensIn",  type: "uint256" },
      { name: "minEthOut", type: "uint256" },
      { name: "deadline",  type: "uint256" },
    ],
    outputs: [],
  },

  // ── Events ────────────────────────────────────────────────────────────────────
  {
    name: "Buy",
    type: "event",
    inputs: [
      { name: "buyer",          type: "address", indexed: true  },
      { name: "ethIn",          type: "uint256", indexed: false },
      { name: "fee",            type: "uint256", indexed: false },
      { name: "tokensOut",      type: "uint256", indexed: false },
      { name: "realEthReserve", type: "uint256", indexed: false },
      { name: "tokenReserve",   type: "uint256", indexed: false },
      { name: "timestamp",      type: "uint256", indexed: false },
    ],
  },
  {
    name: "Sell",
    type: "event",
    inputs: [
      { name: "seller",         type: "address", indexed: true  },
      { name: "tokensIn",       type: "uint256", indexed: false },
      { name: "grossEth",       type: "uint256", indexed: false },
      { name: "fee",            type: "uint256", indexed: false },
      { name: "netEth",         type: "uint256", indexed: false },
      { name: "realEthReserve", type: "uint256", indexed: false },
      { name: "tokenReserve",   type: "uint256", indexed: false },
      { name: "timestamp",      type: "uint256", indexed: false },
    ],
  },
] as const;

/** Minimal ERC-20 ABI for token approvals and balance queries (sell flow). */
export const erc20ApproveAbi = [
  {
    name: "allowance",
    type: "function",
    stateMutability: "view",
    inputs: [
      { name: "owner",   type: "address" },
      { name: "spender", type: "address" },
    ],
    outputs: [{ name: "", type: "uint256" }],
  },
  {
    name: "approve",
    type: "function",
    stateMutability: "nonpayable",
    inputs: [
      { name: "spender", type: "address" },
      { name: "amount",  type: "uint256" },
    ],
    outputs: [{ name: "", type: "bool" }],
  },
  {
    name: "balanceOf",
    type: "function",
    stateMutability: "view",
    inputs: [{ name: "account", type: "address" }],
    outputs: [{ name: "", type: "uint256" }],
  },
] as const;
